/*
 * PageWright — See all pages (S5), vanilla JS, no framework.
 *
 * On a book PDP, fetches the pages that belong to THIS book and renders them as an image grid:
 *   GET {backend}/api/search?type=page&book=<product-handle>&page=<n>
 *   → { results: [ { id, title, type:'page', pageNumber, parentHandle, coverImage } ], total, page, pages }
 *
 * Self-gating: the section is hidden until pages load. A book with page docs reveals the grid; a
 * product with no pages (kits, stickers, single ebooks with no page index) stays hidden — so the
 * same default product template can carry this everywhere with no per-product configuration.
 *
 * Each tile's "Add" button POSTs a real /cart/add.js (S4, wired 2026-07-02) via the shared
 * window.PWPageCart helper (assets/pw-page-cart.js, loaded first by this block's own
 * <script> tags) — see that file for the exact variant/property contract.
 */
(function () {
  'use strict';

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // Favorites deep link (Phase 2): a favorited page's URL is /products/<handle>?pwpage=<n> —
  // on load, auto-open this section and scroll/highlight that page once it's rendered.
  function deepLinkedPage() {
    try {
      var n = parseInt(new URLSearchParams(window.location.search).get('pwpage') || '', 10);
      return n > 0 ? n : null;
    } catch (e) {
      return null;
    }
  }

  function buildTile(result, variantId, bookTitle) {
    var card = el('div', 'pw-book-pages__card');
    card.setAttribute('data-pw-book-pages-card', '');

    var tile = el('div', 'pw-book-pages__tile');
    if (result.coverImage) {
      var img = el('img');
      img.src = result.coverImage;
      img.alt = result.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      tile.appendChild(img);
    }
    // Favorites, Phase 2 (2026-07-21): explicit page-kind mount, same identity already used
    // by this tile's own add-to-cart button below — no new data plumbing.
    if (result.parentHandle && result.pageNumber != null) {
      var favBtn = el('button', 'pw-heart');
      favBtn.type = 'button';
      favBtn.setAttribute('data-pw-fav', '');
      favBtn.setAttribute('data-pw-fav-kind', 'page');
      favBtn.setAttribute('data-pw-fav-handle', result.parentHandle);
      favBtn.setAttribute('data-pw-fav-page', String(result.pageNumber));
      favBtn.setAttribute('data-pw-fav-thumb', result.coverImage || '');
      favBtn.setAttribute('aria-label', 'Favorite');
      favBtn.setAttribute('aria-pressed', 'false');
      var favGlyph = el('span', '', '♡');
      favGlyph.setAttribute('data-pw-fav-glyph', '');
      favGlyph.setAttribute('aria-hidden', 'true');
      favBtn.appendChild(favGlyph);
      tile.appendChild(favBtn);
    }
    card.appendChild(tile);

    var label = result.pageNumber != null ? 'Page ' + result.pageNumber : (result.title || 'Page');
    card.appendChild(el('p', 'pw-book-pages__caption', label));

    // No per-page price on tiles (Paul, 2026-07-13): the live tier legend shows once near the top
    // (snippets/pw-page-pricing-tiers.liquid).
    var addBtn = el('button', 'pw-book-pages__add', 'Add Page to Cart');
    addBtn.type = 'button';
    addBtn.setAttribute('data-pw-add-page', '');
    addBtn.setAttribute('data-page-id', result.id || '');
    addBtn.setAttribute('data-parent-handle', result.parentHandle || '');
    addBtn.setAttribute('data-page-number', result.pageNumber != null ? String(result.pageNumber) : '');
    addBtn.setAttribute('data-cover-image', result.coverImage || '');
    if (bookTitle) addBtn.setAttribute('data-book-title', bookTitle);
    if (variantId) {
      addBtn.setAttribute('data-variant', variantId);
    } else {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
      addBtn.title = 'Single-page buy is not configured for this section instance';
    }
    card.appendChild(addBtn);

    return card;
  }

  function init(root) {
    if (!root || root.dataset.pwInit === 'true') return;
    root.dataset.pwInit = 'true';

    var backendUrl = (root.dataset.backendUrl || '').replace(/\/+$/, '');
    var bookHandle = root.dataset.bookHandle || '';
    var bookTitle = root.dataset.bookTitle || '';
    var variantId = root.dataset.variantId || '';
    var grid = root.querySelector('[data-pw-grid]');
    var toggle = root.querySelector('[data-pw-toggle]');
    var countEl = root.querySelector('[data-pw-count]');
    var moreBtn = root.querySelector('[data-pw-more]');
    if (!backendUrl || !bookHandle || !grid) return;

    var nextPage = 1;
    var totalPages = 1;
    var loading = false;

    grid.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pw-add-page]');
      if (btn && window.PWPageCart) window.PWPageCart.addPageToCart(btn);
    });

    function endpoint(pageNum) {
      return backendUrl + '/api/search?type=page&book=' + encodeURIComponent(bookHandle) + '&page=' + pageNum;
    }

    function appendResults(list) {
      var frag = document.createDocumentFragment();
      for (var i = 0; i < list.length; i++) {
        if (list[i] && list[i].type === 'page') frag.appendChild(buildTile(list[i], variantId, bookTitle));
      }
      grid.appendChild(frag);
    }

    function loadPage(pageNum) {
      if (loading) return Promise.resolve();
      loading = true;
      return fetch(endpoint(pageNum), { headers: { Accept: 'application/json' } })
        .then(function (resp) {
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          return resp.json();
        })
        .then(function (data) {
          var list = (data && Array.isArray(data.results) ? data.results : []).filter(function (r) {
            return r && r.type === 'page';
          });
          // Order by page number (the index recalls lexicographically: 1, 10, 11, … 2, 20).
          list.sort(function (a, b) {
            return (a.pageNumber == null ? 0 : a.pageNumber) - (b.pageNumber == null ? 0 : b.pageNumber);
          });
          if (pageNum === 1 && list.length === 0) {
            // No pages for this product → keep the whole section hidden.
            return;
          }
          totalPages = (data && data.pages) || 1;
          appendResults(list);
          nextPage = pageNum + 1;

          // Reveal the section on the first successful page with results.
          if (pageNum === 1) {
            root.hidden = false;
            if (countEl) {
              var total = (data && data.total) || list.length;
              countEl.textContent = 'See all ' + total + (total === 1 ? ' page' : ' pages');
            }
          }
          if (moreBtn) moreBtn.hidden = nextPage > totalPages;
        })
        .catch(function () {
          /* backend error → leave the section hidden; PDP is unaffected */
        })
        .finally(function () {
          loading = false;
        });
    }

    // Collapsed by default: the header shows a count, the grid opens on toggle.
    if (toggle) {
      toggle.addEventListener('click', function () {
        var open = root.classList.toggle('pw-book-pages--open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        if (nextPage <= totalPages) loadPage(nextPage);
      });
    }

    var targetPage = deepLinkedPage();

    function ensurePageLoaded(target) {
      if (grid.querySelector('[data-page-number="' + target + '"]') || nextPage > totalPages) {
        return Promise.resolve();
      }
      return loadPage(nextPage).then(function () {
        return ensurePageLoaded(target);
      });
    }

    loadPage(1)
      .then(function () {
        if (targetPage == null) return;
        root.classList.add('pw-book-pages--open');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
        return ensurePageLoaded(targetPage);
      })
      .then(function () {
        if (targetPage == null) return;
        var addBtn = grid.querySelector('[data-page-number="' + targetPage + '"]');
        var targetCard = addBtn && addBtn.closest('[data-pw-book-pages-card]');
        if (targetCard) {
          targetCard.classList.add('pw-book-pages__card--highlight');
          targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('pw-book-pages');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
